import { useState } from "react";
import { auth, db } from "../firebase";
import { toast } from "react-toastify";
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  sendPasswordResetEmail,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { useLocation, useNavigate, NavLink } from "react-router-dom";
import "../style/LoginPage.css";
import "../style/authFlow.css";
import googleLogo from "../assets/googleLogo.png";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectPath = location.state?.from || "/student";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [acceptedPolicies, setAcceptedPolicies] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const syncUserWithFirestore = async (user, signupSource = "email") => {
    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      await setDoc(userRef, {
        uid: user.uid,
        email: user.email || "",
        fullName: user.displayName || "",
        role: "student",
        isActive: true,
        onboardingCompleted: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        profile: {
          avatar: "",
          phone: "",
        },
        metadata: {
          lastLogin: serverTimestamp(),
          signupSource,
        },
      });

      return {
        role: "student",
        isActive: true,
        onboardingCompleted: true,
      };
    }

    const data = userSnap.data();

    await setDoc(
      userRef,
      {
        ...data,
        email: user.email || data.email || "",
        fullName: data.fullName || user.displayName || "",
        metadata: {
          ...(data.metadata || {}),
          lastLogin: serverTimestamp(),
        },
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    return {
      role: data.role || "student",
      isActive: data.isActive !== false,
      onboardingCompleted: data.onboardingCompleted !== false,
    };
  };

  const getDashboardPathByRole = (role) => {
    if (role === "admin") return "/admin";
    if (role === "trainer") return "/trainer";
    return "/student";
  };

  const redirectUser = ({ role, onboardingCompleted }) => {
    localStorage.setItem("role", role);

    if (!onboardingCompleted) {
      navigate("/onboarding", { replace: true, state: { message: "Complete onboarding to personalize your dashboard." } });
      return;
    }

    const dashboardPath = getDashboardPathByRole(role);
    const protectedDashboardPaths = ["/admin", "/trainer", "/student"];
    const isProtectedDashboardRedirect = protectedDashboardPaths.includes(redirectPath);

    if (redirectPath !== "/student" && !isProtectedDashboardRedirect) {
      navigate(redirectPath, { state: { message: "Welcome back!" } });
      return;
    }

    navigate(dashboardPath, { replace: true, state: { message: "Welcome back!" } });
  };

  const getFriendlyError = (error) => {
    switch (error.code) {
      case "auth/invalid-credential":
      case "auth/wrong-password":
      case "auth/user-not-found":
        return "Invalid email or password.";
      case "auth/invalid-email":
        return "Please enter a valid email address.";
      case "auth/too-many-requests":
        return "Too many attempts. Please wait a moment and try again.";
      case "auth/popup-closed-by-user":
        return "Google sign-in was closed before completion.";
      case "auth/unauthorized-domain":
        return "This website domain is not authorized in Firebase. Add your current domain in Firebase Authentication > Settings > Authorized domains.";
      default:
        return error.message || "Something went wrong. Please try again.";
    }
  };

  const validateLogin = () => {
    const nextErrors = {};
    const normalizedEmail = email.trim();

    if (!normalizedEmail) nextErrors.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(normalizedEmail)) nextErrors.email = "Enter a valid email";
    if (!password) nextErrors.password = "Password is required";
    if (!acceptedPolicies) {
      nextErrors.policies = "Please accept the Terms and Privacy Policy to continue.";
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!validateLogin()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }

    setLoading(true);

    try {
      const normalizedEmail = email.trim().toLowerCase();

      await setPersistence(
        auth,
        rememberMe ? browserLocalPersistence : browserSessionPersistence
      );

      const res = await signInWithEmailAndPassword(auth, normalizedEmail, password);

      const loginProfile = await syncUserWithFirestore(res.user, "email");

      if (!loginProfile.isActive) {
        await signOut(auth);
        localStorage.removeItem("role");
        toast.error("Your account is inactive. Please contact support.");
        return;
      }

      toast.success("Login successful");

      setTimeout(() => {
        redirectUser(loginProfile);
      }, 1500);
    } catch (error) {
      toast.error(getFriendlyError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (!acceptedPolicies) {
      setFormErrors((currentErrors) => ({
        ...currentErrors,
        policies: "Please accept the Terms and Privacy Policy to continue.",
      }));
      toast.info("Please accept the Terms and Privacy Policy first.");
      return;
    }

    const provider = new GoogleAuthProvider();
    setLoading(true);

    try {
      const res = await signInWithPopup(auth, provider);
      const loginProfile = await syncUserWithFirestore(res.user, "google");

      if (!loginProfile.isActive) {
        await signOut(auth);
        localStorage.removeItem("role");
        toast.error("Your account is inactive. Please contact support.");
        return;
      }

      toast.success("Google login successful");

      setTimeout(() => {
        redirectUser(loginProfile);
      }, 1500);
    } catch (error) {
      toast.error(getFriendlyError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail || !/^\S+@\S+\.\S+$/.test(normalizedEmail)) {
      setFormErrors((currentErrors) => ({
        ...currentErrors,
        email: "Enter your email first, then click forgot password.",
      }));
      toast.info("Enter your email first, then click forgot password.");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, normalizedEmail);
      toast.success("Password reset email sent.");
    } catch (error) {
      toast.error(getFriendlyError(error));
    }
  };

  return (
    <div className="login-container">
      <div className="login-left">
        <div className="form-wrapper">
          <span className="auth-flow-eyebrow">QA Coder account</span>

          <h1 className="welcome-text">Welcome back</h1>
          <p className="subtitle">
            Continue your QA roadmap, practice automation tasks, and open your role-based dashboard.
          </p>
        
          <div className="auth-proof-grid">
            <span>500+ learners</span>
            <span>Live practice</span>
            <span>Interview prep</span>
          </div>

          <form onSubmit={handleLogin}>
            <div className="input-group">
              <label>Email address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                autoComplete="email"
                required
              />
              {formErrors.email && <small className="field-error">{formErrors.email}</small>}
            </div>

            <div className="input-group">
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                autoComplete="current-password"
                required
              />
              {formErrors.password && <small className="field-error">{formErrors.password}</small>}
            </div>

            <div className="form-options">
              <label className="checkbox-wrapper">
                <input
                  type="checkbox"
                  className="custom-checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span className="checkbox-label">Remember for 30 days</span>
              </label>

              <button type="button" className="forgot-password-link" onClick={handleForgotPassword}>
                Forgot password
              </button>
            </div>

            <label className="policy-consent-box">
              <input
                type="checkbox"
                className="custom-checkbox"
                checked={acceptedPolicies}
                onChange={(e) => {
                  setAcceptedPolicies(e.target.checked);
                  if (e.target.checked) {
                    setFormErrors((currentErrors) => {
                      const { policies, ...remainingErrors } = currentErrors;
                      return remainingErrors;
                    });
                  }
                }}
              />
              <span>
                I agree to QA Coder's{" "}
                <NavLink to="/terms" target="_blank" rel="noopener noreferrer">
                  Terms
                </NavLink>
                ,{" "}
                <NavLink to="/privacy-policy" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </NavLink>
                , and understand course payments follow the{" "}
                <NavLink to="/refund-policy" target="_blank" rel="noopener noreferrer">
                  Refund Policy
                </NavLink>
                .
              </span>
            </label>
            {formErrors.policies && <small className="field-error policy-error">{formErrors.policies}</small>}

            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? "Signing in..." : "Sign in"}
            </button>

            <button
              type="button"
              className="btn-google"
              onClick={handleGoogleSignIn}
              disabled={loading}
            >
              <img src={googleLogo} alt="G" />
              Sign in with Google
            </button>
          </form>

          <p className="signup-text">
            Don't have an account? <NavLink to="/signup-page">Sign up</NavLink>
          </p>
        </div>
      </div>

      <div className="login-right">
        <div className="auth-product-panel">
          <span className="auth-panel-kicker">What you unlock</span>
          <h2>One login for learning, practice, and career prep.</h2>
          <div className="auth-panel-steps">
            <div>
              <strong>01</strong>
              <span>Resume your Manual, Automation, or API Testing track.</span>
            </div>
            <div>
              <strong>02</strong>
              <span>Save practice progress and continue from your dashboard.</span>
            </div>
            <div>
              <strong>03</strong>
              <span>Access course enquiries, mentor support, and interview prep.</span>
            </div>
          </div>
          <div className="auth-panel-metric">
            <strong>28+</strong>
            <span>hands-on automation practice modules ready after login</span>
          </div>
        </div>
      </div>
    </div>
  );
}
